import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { isNative, nativeAppleAuthorize } from "@/lib/native";

// Official Apple logo glyph (required by Apple's HIG for a "Sign in with Apple" button).
const AppleGlyph = (props) => (
  <svg viewBox="0 0 384 512" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

/**
 * "Continue with Apple". Renders only when the backend reports Apple is usable for the current
 * surface: native iOS uses the ASAuthorization sheet (apple_native_enabled); the web uses the
 * hosted OAuth flow (apple_enabled). Hidden entirely otherwise, so no dead button ever shows.
 */
export const AppleSignInButton = ({ className = "", full = false, size = "default" }) => {
  const [cfg, setCfg] = useState({ apple_enabled: false, apple_native_enabled: false });
  const [busy, setBusy] = useState(false);
  const native = isNative();

  useEffect(() => {
    let alive = true;
    api
      .get("/auth/config")
      .then((r) => alive && setCfg({
        apple_enabled: Boolean(r.data?.apple_enabled),
        apple_native_enabled: Boolean(r.data?.apple_native_enabled),
      }))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const show = native ? cfg.apple_native_enabled : cfg.apple_enabled;
  if (!show) return null;

  const start = async () => {
    if (native) {
      setBusy(true);
      try {
        const payload = await nativeAppleAuthorize();
        if (!payload.id_token) throw new Error("No identity token");
        await api.post("/auth/apple/native", payload);
        window.location.href = "/dashboard";
      } catch (e) {
        toast.error(e?.response ? errMsg(e) : "Apple sign-in was cancelled");
        setBusy(false);
      }
      return;
    }
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/apple/start`;
  };

  return (
    <Button
      onClick={start}
      size={size}
      disabled={busy}
      className={cn(
        "bg-foreground text-background hover:bg-foreground/90 gap-2",
        full && "w-full",
        className,
      )}
      data-testid="apple-signin-button"
    >
      <AppleGlyph className="w-4 h-4 -mt-0.5" />
      Continue with Apple
    </Button>
  );
};
